import { TextFieldWithCheckbox } from "@/controls/form-fields/CheckboxField";
import { ErrorModal, MesfModal } from "@dexteel/mesf-core";
import {
	Button,
	CircularProgress,
	Grid,
	Snackbar,
	TextField,
} from "@material-ui/core";
import MuiAlert from "@material-ui/lab/Alert";
import React, { useEffect, useState } from "react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import {
	CATALOG_ELEMENT_INITIAL_VALUES,
	CatalogElement,
} from "../../../models/CatalogElement";
import {
	getCatalogElement,
	upsertCatalogElement,
} from "../../../repositories/CatalogElementRepository";
import { useModalStyles } from "../../../styles/modalStyles";
type Props = {
	elementId: number | null;
	show: boolean;
	onHide: (shouldUpdate: boolean) => void;
};
export const EditElement = ({ elementId, show, onHide }: Props) => {
	const classes = useModalStyles();
	const [open, setOpen] = useState(false);
	const [error, setError] = useState("");
	const [isLoading, setIsLoading] = useState(false);
	const [isSubmitLoading, setIsSubmitLoading] = useState(false);
	const {
		register,
		control,
		setValue,
		handleSubmit,
		formState: { errors },
	} = useForm<CatalogElement>({
		defaultValues: CATALOG_ELEMENT_INITIAL_VALUES,
	});
	const handleClose = (event?: React.SyntheticEvent, reason?: string) => {
		if (reason === "clickaway") {
			return;
		}
		setOpen(false);
	};
	const onSubmit: SubmitHandler<CatalogElement> = async (data) => {
		setIsSubmitLoading(true);
		const res = await upsertCatalogElement(data);
		if (res.ok) {
			const userId = res.data.tables[0].rows[0].UserId;
			onHide(true);
			setOpen(true);
		} else {
			setError(res.message);
		}
		setIsSubmitLoading(false);
	};
	useEffect(() => {
		if (show) {
			setIsSubmitLoading(false);
			(async () => {
				setIsLoading(true);
				const response = await getCatalogElement(elementId);
				if (response.ok) {
					const elementSelected = response.data.tables[0].rows[0];
					if (elementSelected) {
						setValue("ElementId", elementSelected.ElementId);
						setValue("ElementName", elementSelected.ElementName);
						setValue("Decimals", elementSelected.Decimals);
						setValue("Order", elementSelected.Order);
						setValue("IsActive", elementSelected.IsActive);
						setIsLoading(false);
					}
				} else {
					setError(response.message);
				}
			})();
		}
	}, [show]);
	return (
		<>
			<MesfModal
				open={show}
				handleClose={() => onHide(false)}
				maxWidth="md"
				aria-labelledby="contained-modal-title-center"
				title="	EDIT ELEMENT"
			>
				<form onSubmit={handleSubmit(onSubmit)}>
					<MesfModal.Content
						dividers
						className={classes.modalBody}
						style={{ padding: "15px 30px" }}
					>
						{isLoading && (
							<Grid
								container
								justifyContent="center"
								alignItems="center"
								style={{ minHeight: "300px" }}
							>
								<CircularProgress />
							</Grid>
						)}
						{!isLoading && (
							<>
								<Grid container spacing={1} alignContent="center">
									<Grid item xs={12} md={6}>
										<TextField
											{...register("ElementName", { required: true })}
											label="Name"
											variant="outlined"
											error={!!errors.ElementName}
											fullWidth
											margin="dense"
											autoComplete="off"
										/>
									</Grid>
									<Grid item xs={12} md={6}>
										<TextField
											{...register("Decimals", { required: true })}
											label="# Decimals"
											variant="outlined"
											error={!!errors.Decimals}
											fullWidth
											margin="dense"
											type="number"
											autoComplete="off"
											onFocus={(e) => e.target.select()}
										/>
									</Grid>
									<Grid item xs={12} md={6}>
										<TextField
											{...register("Order", { required: true })}
											label="Order"
											variant="outlined"
											error={!!errors.Order}
											fullWidth
											margin="dense"
											type="number"
											autoComplete="off"
											onFocus={(e) => e.target.select()}
										/>
									</Grid>
									<Grid item xs={12} md={6} alignContent="center">
										<Controller
											name="IsActive"
											control={control}
											render={({ field }) => (
												<TextFieldWithCheckbox
													checkboxValue={field.value}
													onCheckboxChange={field.onChange}
													label="Enabled"
													className={classes.checkbox}
												/>
											)}
										/>
									</Grid>
								</Grid>
							</>
						)}
					</MesfModal.Content>
					<MesfModal.Actions style={{ padding: "20px 30px 30px" }}>
						<Grid container spacing={2} justifyContent="flex-end">
							<Grid item md={3} xs={12} style={{ margin: 0 }}>
								<Button
									fullWidth
									variant="contained"
									color="default"
									onClick={() => onHide(false)}
								>
									Cancel
								</Button>
							</Grid>
							<Grid item md={3} xs={12} style={{ margin: 0 }}>
								<Button
									fullWidth
									startIcon={
										isSubmitLoading ? <CircularProgress size="1rem" /> : <></>
									}
									disabled={isSubmitLoading}
									variant="contained"
									color="primary"
									type="submit"
								>
									Save
								</Button>
							</Grid>
						</Grid>
					</MesfModal.Actions>
				</form>
			</MesfModal>
			<Snackbar open={open} autoHideDuration={2500} onClose={handleClose}>
				<MuiAlert
					elevation={6}
					variant="filled"
					severity="success"
					onClose={handleClose}
				>
					The element was edited successfully
				</MuiAlert>
			</Snackbar>
			<ErrorModal error={error} onHide={() => setError("")} />
		</>
	);
};
